import { AlertMessage } from '@eposnow/ui-core';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomTimePeriod,
  useCreateCustomTimePeriod,
  useDeleteCustomTimePeriod,
  useUpdateCustomTimePeriod,
} from '../api/customTimePeriods';
import { ErrorObject } from '../api/fetch';
import { comboStyle } from '../constants';
import { UserContext } from '../context/UserContext';
import { dateRange, DateRangeConfig } from '../helpers/dateUtils';
import TitledDrawer from './TitledDrawer';

const NAME_MAX_LENGTH = 100;

const createBlankCustomTimePeriod = (
  dateRangeConfig: DateRangeConfig
): CustomTimePeriod => {
  const range = dateRange('today', undefined, undefined, undefined, dateRangeConfig);
  return {
    id: '',
    name: '',
    startDate: new Date(range.startDate),
    endDate: new Date(range.endDate),
  };
};

type CustomPeriodPanelProps = {
  initialPeriod?: CustomTimePeriod;
  dateRangeConfig: DateRangeConfig;
  onClose: () => void;
  onSave?: (period: CustomTimePeriod) => void;
  onDelete?: (periodId: string) => void;
};

const emptyErrors = {
  name: '',
  startDate: '',
  endDate: '',
};

const CustomPeriodPanel = ({
  initialPeriod,
  dateRangeConfig,
  onClose,
  onSave,
  onDelete,
}: CustomPeriodPanelProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { locale } = useContext(UserContext);
  const createCustomTimePeriod = useCreateCustomTimePeriod();
  const updateCustomTimePeriod = useUpdateCustomTimePeriod();
  const deleteCustomTimePeriod = useDeleteCustomTimePeriod();

  const [draft, setDraft] = useState<CustomTimePeriod>(
    () => initialPeriod ?? createBlankCustomTimePeriod(dateRangeConfig)
  );
  const [fieldErrors, setFieldErrors] = useState(emptyErrors);
  const [apiError, setApiError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openDateFrom, setOpenDateFrom] = useState(false);
  const [openDateTo, setOpenDateTo] = useState(false);

  const isEditing = Boolean(draft.id);

  useEffect(() => {
    setDraft(initialPeriod ?? createBlankCustomTimePeriod(dateRangeConfig));
    setFieldErrors(emptyErrors);
    setApiError('');
  }, [initialPeriod, dateRangeConfig]);

  const hasErrors = useCallback(
    (key: keyof typeof emptyErrors) => Boolean(fieldErrors[key]),
    [fieldErrors]
  );

  const fromPicker = useCallback(
    () => (
      <InputAdornment position="end" onClick={() => setOpenDateFrom(true)}>
        <IconButton aria-label="Start date (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('startDate') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [hasErrors]
  );

  const toPicker = useCallback(
    () => (
      <InputAdornment position="end" onClick={() => setOpenDateTo(true)}>
        <IconButton aria-label="End date (show calendar)" sx={{ left: '12px' }}>
          {hasErrors('endDate') ? <ErrorOutlineIcon color="error" /> : <EventOutlinedIcon />}
        </IconButton>
      </InputAdornment>
    ),
    [hasErrors]
  );

  const validate = useCallback(() => {
    const nextErrors = { ...emptyErrors };
    const trimmedName = draft.name.trim();
    const validFrom = !Number.isNaN(draft.startDate.getTime());
    const validTo = !Number.isNaN(draft.endDate.getTime());

    if (!trimmedName) {
      nextErrors.name = t('components.datePicker.enterValidCustomPeriodName');
    }
    if (!validFrom) {
      nextErrors.startDate = t('components.datePicker.enterValidDate');
    }
    if (!validTo) {
      nextErrors.endDate = t('components.datePicker.enterValidDate');
    }
    if (validFrom && validTo) {
      // extra hour so a 31-day range that includes DST still fits
      const maxDayLimitHours = 31 * 24 + 1;
      const millisPerHour = 1000 * 60 * 60;
      const dateGap = draft.endDate.getTime() - draft.startDate.getTime();
      if (dateGap < 0) {
        nextErrors.startDate = t('components.datePicker.startDateMayNotBeAfterEnd');
      }
      if (dateGap === 0) {
        nextErrors.endDate = t('components.datePicker.endDateMayNotBeBeforeStart');
      }
      if (dateGap / millisPerHour > maxDayLimitHours) {
        nextErrors.endDate = t('components.datePicker.dateRangeLimitedToOneMonth');
      }
    }

    setFieldErrors(nextErrors);
    return !nextErrors.name && !nextErrors.startDate && !nextErrors.endDate;
  }, [draft, t]);

  const getApiErrorMessage = (err: unknown) => {
    const errorObject = err as ErrorObject;
    if (errorObject?.code === 409) {
      return t('components.datePicker.customTimePeriodLimitReached');
    }

    return t('components.error.tryAgainLater');
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setApiError('');
    try {
      const payload = {
        name: draft.name.trim(),
        startDate: draft.startDate,
        endDate: draft.endDate,
      };
      const saved = isEditing
        ? await updateCustomTimePeriod(draft.id, payload)
        : await createCustomTimePeriod(payload);
      onSave?.(saved);
      onClose();
    } catch (err) {
      setApiError(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing) return;
    setIsDeleting(true);
    setApiError('');
    try {
      await deleteCustomTimePeriod(draft.id);
      onDelete?.(draft.id);
      onClose();
    } catch (err) {
      setApiError(getApiErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const fullDayHelper = `${t('components.datePicker.aFullDayIsFrom')} ${dateRangeConfig.daysStartAt
    .toString(10)
    .padStart(2, '0')}:00 ${t('components.datePicker.to')} ${dateRangeConfig.daysStartAt
    .toString(10)
    .padStart(2, '0')}:00`;

  const bottomToolbar = (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: 'auto',
          padding: '16px',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          sx={{ width: '100%', marginLeft: '0px', marginBottom: '8px' }}
          onClick={handleSave}
          disabled={isSaving || isDeleting}
          data-qa-id="customPeriodSave"
        >
          {isSaving ? t('actions.saving') : t('actions.save')}
        </Button>
        {isEditing && (
          <Button
            variant="outlined"
            sx={{ width: '100%', marginBottom: '8px' }}
            onClick={handleDelete}
            disabled={isSaving || isDeleting}
            data-qa-id="customPeriodDelete"
          >
            {isDeleting ? t('actions.deleting') : t('actions.delete')}
          </Button>
        )}
        <Button
          variant="text"
          sx={{ width: '100%' }}
          onClick={onClose}
          disabled={isSaving || isDeleting}
          data-qa-id="customPeriodCancel"
        >
          {t('actions.cancel')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <TitledDrawer
      title={t('components.datePicker.customTimePeriod')}
      onClose={onClose}
      bottomToolbar={bottomToolbar}
      bottomToolbarMarginTop="16px"
    >
      <Grid
        item
        sx={{
          paddingLeft: '4px',
          paddingRight: '4px',
        }}
      >
        {apiError && (
          <AlertMessage type="error" locale={locale} theme={theme} styles={{ marginBottom: 2 }}>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>{apiError}</p>
          </AlertMessage>
        )}
        <TextField
          sx={{ marginBottom: '16px' }}
          size="small"
          label={t('components.datePicker.customTimePeriod')}
          value={draft.name}
          id="custom-period-name"
          fullWidth
          inputProps={{ maxLength: NAME_MAX_LENGTH, 'data-qa-id': 'customPeriodName' }}
          error={hasErrors('name')}
          helperText={fieldErrors.name}
          onChange={(ev) => setDraft((prev) => ({ ...prev, name: ev.target.value }))}
        />
        <DateTimePicker
          label={t('components.datePicker.startDate')}
          value={draft.startDate}
          onChange={(date) =>
            setDraft((prev) => ({
              ...prev,
              startDate: date ?? prev.startDate,
            }))
          }
          open={openDateFrom}
          onOpen={() => setOpenDateFrom(true)}
          onClose={() => setOpenDateFrom(false)}
          slots={{
            inputAdornment: fromPicker,
          }}
          slotProps={{
            textField: {
              size: 'small',
              error: hasErrors('startDate'),
              helperText: fieldErrors.startDate || fullDayHelper,
              style: comboStyle,
              inputProps: {
                'data-qa-id': 'customPeriodStartDateTime',
              },
            },
          }}
          format={t('components.datePicker.dateTimeFormat')}
        />
        <DateTimePicker
          label={t('components.datePicker.endDate')}
          value={draft.endDate}
          onChange={(date) =>
            setDraft((prev) => ({
              ...prev,
              endDate: date ?? prev.endDate,
            }))
          }
          open={openDateTo}
          onOpen={() => setOpenDateTo(true)}
          onClose={() => setOpenDateTo(false)}
          format={t('components.datePicker.dateTimeFormat')}
          slots={{
            inputAdornment: toPicker,
          }}
          slotProps={{
            textField: {
              size: 'small',
              error: hasErrors('endDate'),
              style: comboStyle,
              helperText: fieldErrors.endDate || fullDayHelper,
              inputProps: {
                'data-qa-id': 'customPeriodEndDateTime',
              },
            },
          }}
        />
      </Grid>
    </TitledDrawer>
  );
};

export default CustomPeriodPanel;
